import type { Application } from '@/modules/applications/types';
import type { DocumentRecord } from '@/modules/documents/types';
import type { Inspection } from '@/modules/inspections/types';
import type { Notification } from '@/modules/notifications/types';
import type { Payment } from '@/modules/payments/types';
import type { CashReceipt } from '@/modules/cash-and-billing/types';
import { companies, createRandom, dcLocation, streets } from './random';
import type { Permit, PermitLocation } from '@/modules/permits/types';

const address = (street: string) => `${street}, Washington, DC, 20003, USA`;

export const applications: Application[] = (() => {
  const r = createRandom(9123);
  const types = [
    'Asbestos Abatement Permit',
    'Building Permit',
    'Public Space Permit',
    'Business License',
    'Special Event Permit',
    'Demolition Permit',
  ] as const;
  const statuses = [
    'draft',
    'submitted',
    'inReview',
    'actionRequired',
    'issued',
    'lapsed',
    'rejected',
  ] as const;
  return Array.from({ length: 200 }, (_, i) => {
    const submittedAt = r.pastDate(400);
    return {
      id: `app-${i + 1}`,
      trackingNumber: `TR #${9123 + i}`,
      permitNumber: `PN #${1233656 + i * 7}`,
      applicant: r.pick(companies),
      permitType: r.pick(types),
      status: r.pick(statuses),
      ward: `Ward ${r.int(1, 8)}`,
      address: address(r.pick(streets)),
      submittedAt,
      updatedAt: r.pastDate(30),
      ...dcLocation(r),
    };
  });
})();

export const inspections: Inspection[] = (() => {
  const r = createRandom(4242);
  const types = ['Framing', 'Electrical rough-in', 'Plumbing', 'Final', 'Fire safety'] as const;
  const statuses = ['scheduled', 'passed', 'failed', 'cancelled'] as const;
  const inspectors = ['J. Rivera', 'M. Chen', 'A. Okafor', 'S. Patel'] as const;
  return Array.from({ length: 64 }, (_, i) => ({
    id: `insp-${i + 1}`,
    inspectionNumber: `IN #${5100 + i}`,
    permitNumber: `PN #${1233656 + i * 11}`,
    inspectionType: r.pick(types),
    status: r.pick(statuses),
    scheduledFor: r.futureDate(60),
    inspector: r.pick(inspectors),
    address: address(r.pick(streets)),
    ...dcLocation(r),
  }));
})();

export const payments: Payment[] = (() => {
  const r = createRandom(777);
  const descriptions = [
    'Application fee',
    'Permit issuance fee',
    'Inspection fee',
    'Late renewal fee',
  ] as const;
  const statuses = ['paid', 'due', 'overdue', 'refunded'] as const;
  return Array.from({ length: 48 }, (_, i) => ({
    id: `pay-${i + 1}`,
    invoiceNumber: `INV-${20260 + i}`,
    permitNumber: `PN #${1233656 + i * 13}`,
    description: r.pick(descriptions),
    amount: r.int(25, 2400),
    status: r.pick(statuses),
    dueDate: r.futureDate(45),
  }));
})();

export const cashReceipts: CashReceipt[] = [
  {
    id: '10001',
    trackingNo: 10001,
    certificateType: 'CASH',
    certificateNumber: 'CR-2026-001',
    certificateStatusId: 3,
    certificateStatusName: 'Verified',
    certificateReceiptFor: 'Permit fee',
    applicationTrackingNumber: 'TR #9123',
    amountUsd: 450,
    amountDetails: {
      certificateAmountUsd: 450,
      consumedAmountUsd: 125,
      availableAmountUsd: 325,
    },
    issueDate: '2026-06-10',
    expiryDate: '2027-06-10',
    issuingAuthority: 'Cashier',
    issuedTo: 'Northwest Construction',
    verified: 'Yes',
  },
  {
    id: '10002',
    trackingNo: 10002,
    certificateType: 'CASH',
    certificateNumber: 'CR-2026-002',
    certificateStatusId: 1,
    certificateStatusName: 'Submitted',
    certificateReceiptFor: 'Application fee',
    applicationTrackingNumber: 'TR #9124',
    amountUsd: 225,
    issueDate: '2026-07-02',
    issuingAuthority: 'Cashier',
    issuedTo: 'District Building Group',
    verified: 'No',
  },
  {
    id: '10003',
    trackingNo: 10003,
    certificateType: 'CASH',
    certificateNumber: 'CR-2026-003',
    certificateStatusId: 2,
    certificateStatusName: 'Pending Approval',
    certificateReceiptFor: 'Permit fee',
    applicationTrackingNumber: 'TR #9125',
    amountUsd: 760,
    issueDate: '2026-07-19',
    expiryDate: '2027-07-19',
    issuingAuthority: 'Cashier',
    issuedTo: 'Capital Renovations',
    verified: 'No',
  },
  {
    id: '10004',
    trackingNo: 10004,
    certificateType: 'BOND',
    certificateNumber: 'CR-2026-004',
    certificateStatusId: 4,
    certificateStatusName: 'Rejected',
    certificateReceiptFor: 'Application fee',
    applicationTrackingNumber: 'TR #9126',
    amountUsd: 150,
    issueDate: '2026-08-03',
    issuingAuthority: 'Cashier',
    issuedTo: 'Eastern Design LLC',
    verified: 'No',
  },
  {
    id: '10005',
    trackingNo: 10005,
    certificateType: 'Certificate of Deposit',
    certificateNumber: 'CR-2026-005',
    certificateStatusId: 2,
    certificateStatusName: 'Pending Approval',
    certificateReceiptFor: 'Permit fee',
    applicationTrackingNumber: 'TR #9125',
    amountUsd: 860,
    issueDate: '2026-07-19',
    expiryDate: '2027-07-19',
    issuingAuthority: 'Cashier',
    issuedTo: 'Capital Renovations',
    verified: 'No',
  },
];

export const documents: DocumentRecord[] = (() => {
  const r = createRandom(31337);
  const categories = [
    'Site plan',
    'Structural drawings',
    'Insurance certificate',
    'Contractor license',
    'Photo',
  ] as const;
  const statuses = ['approved', 'pendingReview', 'rejected'] as const;
  return Array.from({ length: 56 }, (_, i) => {
    const category = r.pick(categories);
    return {
      id: `doc-${i + 1}`,
      name: `${category.replace(/\s+/g, '-').toLowerCase()}-${i + 1}.pdf`,
      permitNumber: `PN #${1233656 + i * 5}`,
      category,
      status: r.pick(statuses),
      uploadedAt: r.pastDate(120),
    };
  });
})();

export const notifications: Notification[] = (() => {
  const r = createRandom(5150);
  const titles = [
    'NOI Approved',
    'Inspection scheduled',
    'Payment received',
    'Action required: upload documents',
  ] as const;
  const types = ['Asbestos Abatement Permit', 'Building Permit', 'Public Space Permit'] as const;
  return Array.from({ length: 6 }, (_, i) => ({
    id: `ntf-${i + 1}`,
    title: r.pick(titles),
    description: 'Description',
    applicant: r.pick(companies),
    permitType: r.pick(types),
    createdAt: r.pastDate(60),
    unread: i < 3,
    href: `/applications/all?q=${encodeURIComponent(`TR #${9123 + i}`)}`,
  }));
})();

export const permits: Permit[] = (() => {
  const r = createRandom(9123);

  const permitTypes = [
    'Asbestos Abatement Permit',
    'Building Permit',
    'Public Space Permit',
    'Business License',
    'Special Event Permit',
    'Demolition Permit',
  ] as const;

  const statuses = [
    { id: 1, code: 'DRAFT' },
    { id: 2, code: 'SUBMITTED' },
    { id: 5, code: 'ISSUED' },
    { id: 6, code: 'LAPSED' },
    { id: 7, code: 'REJECTED' },
  ] as const;

  const companies = [
    'ABC Construction LLC',
    'Capital Development Group',
    'Green Earth Solutions',
    'Metro Building Services',
    'Urban Planning Associates',
    'Premier Demolition Inc.',
    'Citywide Contractors',
    'Evergreen Property Group',
    'Summit Engineering LLC',
    'Blue Sky Events',
  ];

  const firstNames = [
    'James',
    'Michael',
    'Sarah',
    'Emily',
    'David',
    'Olivia',
    'Daniel',
    'Sophia',
    'Robert',
    'Emma',
  ];

  const lastNames = [
    'Smith',
    'Johnson',
    'Williams',
    'Brown',
    'Jones',
    'Garcia',
    'Miller',
    'Davis',
    'Wilson',
    'Taylor',
  ];

  const streets = [
    'Main Street',
    'Pennsylvania Avenue',
    'Georgia Avenue',
    'Wisconsin Avenue',
    'Connecticut Avenue',
    'Massachusetts Avenue',
    '14th Street NW',
    'H Street NW',
    'K Street NW',
    'New York Avenue',
  ];

  const permitLocation = (): PermitLocation => ({
    locationTypeId: r.int(1, 3),
    locationDesc: `${r.int(100, 9999)} ${r.pick(streets)}`,
    countryId: 1,
    stateId: r.int(1, 50),
    city: 'Washington',
    wardId: r.int(1, 8),
    ancId: r.int(1, 8),
    unitNumber: r.pick(['', 'Suite 100', 'Unit 2', 'Floor 3']),
    zip: `200${r.int(1, 20).toString().padStart(2, '0')}`,
    latitude: 38.9072 + r.int(-1000, 1000) / 100000,
    longitude: -77.0369 + r.int(-1000, 1000) / 100000,
  });

  return Array.from({ length: 200 }, (_, i): Permit => {
    const createdDate = r.pastDate(400);
    const submittedDate = r.pastDate(300);
    const lastUpdatedDate = r.pastDate(30);
    const status = r.pick(statuses);
    const applicantName = `${r.pick(firstNames)} ${r.pick(lastNames)}`;
    const businessName = r.pick(companies);
    const permitTypeName = r.pick(permitTypes);

    const latitude = 38.9072 + r.int(-1000, 1000) / 100000;
    const longitude = -77.0369 + r.int(-1000, 1000) / 100000;

    return {
      id: `permit-${i + 1}`,
      trackingNumber: 9123 + i,
      permitNumber: status.code === 'ISSUED' ? `PER-${String(1233656 + i * 7)}` : undefined,
      permiteeDetails: {
        firstName: applicantName.split(' ')[0],
        lastName: applicantName.split(' ')[1],
        businessName,
      },
      permiteeName: applicantName,
      permitIssueDate: status.code === 'ISSUED' ? new Date(r.pastDate(180)) : undefined,
      isExactMatch: r.pick([true, true, true, false]),
      applicantName,
      businessName,
      locations: [permitLocation()],
      statusId: status.id,
      permitStatusCode: status.code,
      createdDate: new Date(createdDate),
      submittedDate: ['DRAFT'].includes(status.code) ? undefined : new Date(submittedDate),
      ownerName: `${r.pick(firstNames)} ${r.pick(lastNames)}`,
      permitTypeName,
      lastUpdatedDate: new Date(lastUpdatedDate),
      lastUpdatedBy: `${r.pick(firstNames)} ${r.pick(lastNames)}`,
      coordinatedList: [
        { latitude, longitude },
        {
          latitude: latitude + 0.001,
          longitude: longitude + 0.001,
        },
      ],
    };
  });
})();
